import {Condition} from '../contracts/report-definition.types';
import {ReportTaxonomy, fieldByFilterField, operatorLabel, resolverKindFor} from '../contracts/report-taxonomy.types';
import {REMAINING_WORK_YES, remainingWorkSelection} from './remainingWorkCondition';

export type LabelResolver = (value: string) => string | undefined;

export interface SummaryLabels {
	or: string;
	selected: (n: number) => string;
	yes: string;
	no: string;
}

const DEFAULT_LABELS: SummaryLabels = {
	or: 'or',
	selected: n => `${n} selected`,
	yes: 'Yes',
	no: 'No',
};

export interface FormatConditionSummaryOptions {
	resolveLabel?: LabelResolver;
	labels?: SummaryLabels;
}

export function formatConditionSummary(
	condition: Condition,
	taxonomy: ReportTaxonomy,
	opts?: FormatConditionSummaryOptions
): string {
	const resolveLabel = opts?.resolveLabel;
	const labels = opts?.labels ?? DEFAULT_LABELS;
	const field = fieldByFilterField(taxonomy, condition.field);
	const name = field?.label ?? condition.field;
	const op = operatorLabel(taxonomy, condition.operator);
	const values = condition.values ?? [];

	if (values.length === 0) {
		return name;
	}

	if (field?.dataType === 'boolean') {
		const yes = values[0] === 'true';
		return `${name} ${op} ${yes ? labels.yes : labels.no}`;
	}

	// Remaining work is a number field the reader answers Yes or No to, like Bug and Blocked, so its
	// summary has to read that way too - otherwise the tile says "greater than 0" while the control
	// beside it offers Yes and No. Which comparison means which answer is `remainingWorkSelection`'s
	// business, not this function's, so a stored comparison the control cannot express (a
	// "more than 8 hours left" an agent wrote, say) selects nothing here and falls through to the
	// numeric branch below, which still shows the comparison it really is. The operator shown is IS
	// rather than the stored comparison, for the same reason the answer is Yes: "greater than Yes"
	// is not a sentence, and IS is what the equivalent boolean tiles store.
	if (field && resolverKindFor(taxonomy, field) === 'remainingWork') {
		const [picked] = remainingWorkSelection(condition);
		if (picked !== undefined) {
			const answer = picked === REMAINING_WORK_YES ? labels.yes : labels.no;
			return `${name} ${operatorLabel(taxonomy, 'IS')} ${answer}`;
		}
	}

	// Numeric/date/duration fields are never id-backed — the stored values ARE the human-readable
	// values (e.g. "8", "2024-01-01"). Render them directly, before the id-resolve/count logic
	// below, so a comparison condition never collapses to a misleading "N selected".
	if (field?.dataType === 'number' || field?.dataType === 'date' || field?.dataType === 'duration') {
		if (values.length === 1) {
			return `${name} ${op} ${values[0]}`;
		}
		return `${name} ${op} ${values.join(` ${labels.or} `)}`;
	}

	const resolved = values.map(v => (resolveLabel ? resolveLabel(v) : v));
	const anyUnresolved = resolved.some(r => r === undefined);

	if (anyUnresolved || values.length > 3) {
		return `${name}: ${labels.selected(values.length)}`;
	}

	const resolvedLabels = resolved as string[];
	if (resolvedLabels.length === 1) {
		return `${name} ${op} ${resolvedLabels[0]}`;
	}
	return `${name} ${op} ${resolvedLabels.join(` ${labels.or} `)}`;
}
